import { USER_PROFILE_INFO } from '@utils/loginHelper';
import GrabStorage from 'grab-storage';

interface UserProfileLink {
  link_entity_business_line: string;
  link_entity_type: string;
  link_entity_id: string;
}

interface UserProfile {
  grab_id?: string;
  parent_entity_id?: string;
  grab_food_entity_id?: string;
  role?: string;
  merchant_grab_id?: string;
  country?: number;
  is_observer?: boolean;
  is_on_mex_level?: boolean;
  links?: UserProfileLink[];
}

interface UserProfileInfo {
  user_profile?: UserProfile;
  business_line_ids?: string[];
  city_id?: number;
  country?: string;
  regd_country_id?: number;
}

export interface ProfileInfo {
  USER_GRAB_ID: string | null;
  PARENT_ENTITY_ID: string | null;
  GRABFOOD_ENTITY_ID: string | null;
  USER_ROLE: string | null;
  MEX_TYPE: string[] | null;
  LOGIN_TYPE: string | null;
  MERCHANT_GRAB_ID: string | null;
  STORE_GRAB_ID: string | null;
  CITY_ID: number | null;
  COUNTRY: string | null;
  COUNTRY_ID: number | null;
  IS_OBSERVER: boolean | null;
  IS_ON_MEX_LEVEL: boolean | null;
}

const getDefaultProfileInfo = (): ProfileInfo => ({
  USER_GRAB_ID: null,
  PARENT_ENTITY_ID: null,
  GRABFOOD_ENTITY_ID: null,
  USER_ROLE: null,
  MEX_TYPE: null,
  LOGIN_TYPE: null,
  MERCHANT_GRAB_ID: null,
  STORE_GRAB_ID: null,
  CITY_ID: null,
  COUNTRY: null,
  COUNTRY_ID: null,
  IS_OBSERVER: null,
  IS_ON_MEX_LEVEL: null,
});

const getLoginType = (userProfile: UserProfile): string | null => {
  if (!userProfile) {
    return null;
  }

  const userProfileLinks = userProfile.links || [];
  const userProfileLinkGPData = userProfileLinks.find((data: UserProfileLink) => data.link_entity_business_line === 'GP');

  if (!userProfileLinkGPData) {
    return null;
  }

  const userType = userProfileLinkGPData.link_entity_type;

  // Based on the sample data, link_entity_type can be "6" or other values
  // This maps to different user types
  if (userType === '6') {
    return 'STORE';
  }

  // Add other mappings as needed based on business logic
  return 'MERCHANT';
};

const getStoreGrabId = (userProfile: UserProfile): string | null => {
  if (!userProfile) {
    return null;
  }

  const userProfileLinks = userProfile.links || [];
  const storeLink = userProfileLinks.find((data: UserProfileLink) => data.link_entity_business_line === 'GF');

  return storeLink?.link_entity_id || null;
};

export const getProfileInfoForInstrumentation = (): ProfileInfo => {
  try {
    const rawProfileInfo = GrabStorage.getItem(USER_PROFILE_INFO);
    const userProfileInfo: UserProfileInfo | null = JSON.parse(
      !rawProfileInfo || rawProfileInfo === 'undefined' ? '{}' : rawProfileInfo,
    );

    if (!userProfileInfo) {
      return getDefaultProfileInfo();
    }

    const userProfile = userProfileInfo?.user_profile;

    if (!userProfile) {
      return getDefaultProfileInfo();
    }

    const profileInfo: ProfileInfo = {
      USER_GRAB_ID: userProfile.grab_id || null,
      PARENT_ENTITY_ID: userProfile.parent_entity_id || null,
      GRABFOOD_ENTITY_ID: userProfile.grab_food_entity_id || null,
      USER_ROLE: userProfile.role || null,
      MEX_TYPE: userProfileInfo.business_line_ids || null,
      LOGIN_TYPE: getLoginType(userProfile),
      MERCHANT_GRAB_ID: userProfile.merchant_grab_id || null,
      STORE_GRAB_ID: getStoreGrabId(userProfile),
      CITY_ID: userProfileInfo.city_id || null,
      COUNTRY: userProfileInfo.country || null,
      COUNTRY_ID: userProfileInfo.regd_country_id || userProfile.country || null,
      IS_OBSERVER: userProfile.is_observer || null,
      IS_ON_MEX_LEVEL: userProfile.is_on_mex_level || null,
    };

    return profileInfo;
  } catch (error) {
    console.info('Error getting profile info for instrumentation:', error);
    return getDefaultProfileInfo();
  }
};
